import {
  Box,
  Button,
  Typography,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  Chip,
  InputAdornment,
} from '@mui/material';
import { useBooking } from '../../../../context/bookingContext';
import dayjs from 'dayjs';
import EditIcon from '@mui/icons-material/Edit';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import AddIcon from '@mui/icons-material/Add';
import { useEffect, useState, useRef, useImperativeHandle, forwardRef } from 'react';
import { useAuth } from '../../../../context/authContext';
import LockIconWrapper from '../../../components/LockIconWrapper';
import SpecialLineItem from './components/SpecialLineItem';
import useVoucherRedemption from '../../../../hooks/useVoucherRedemption';
import useVoucher from '../../../../hooks/useVoucher';
import useTemporaryResevation from '../../../../hooks/useTemporaryResevation';
import { toast } from 'react-toastify';
import DeleteIcon from '@mui/icons-material/Delete';
import DiscountIcon from '@mui/icons-material/Discount';
import { isOfflinePaymentAllowedForPhone } from '../../../../util/phonePaymentPolicy';

const formatDuration = ms => {
  if (!ms) return '-';
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  return `${hours} Stunden ${minutes > 0 ? `und ${minutes} Minuten` : ''}`;
};

const Overview = forwardRef((props, ref) => {
  const { tempReservationId, reservationCartSummary } = props;
  const {
    selectedDate,
    selectedTime,
    selectedDuration,
    selectedCategories,
    selectedPackage,
    selectedUpsells,
    selectedResource,
    selectedEntertainmentObject,
    customerData,
    calculateTotalAmount,
    priceOverrides,
    updateLineItemPrice,
    priceOverride,
    setPriceOverride,
    getCurrentLineItems,
    setPriceOverrides,
    specialLineItems,
    setSpecialLineItems,
    resources,
    products,
    selectedProduct,
    defaultComment,
  } = useBooking();

  const commentRef = useRef(null);

  // Seed the (uncontrolled) comment field from the injected default booking comment.
  // Only fills when the field is still empty so a user-typed comment is never clobbered.
  useEffect(() => {
    if (!defaultComment) return;
    if (commentRef.current && !commentRef.current.value) {
      commentRef.current.value = defaultComment;
    }
  }, [defaultComment]);
  const redeemCodeRef = useRef(null);
  const voucherPinRef = useRef(null);
  const capacityParticipantCountRef = useRef(null);
  const capacityLaneCountRef = useRef(null);

  const { checkRightAny, preferences } = useAuth();
  const { applyVoucher, removeVoucher } = useVoucherRedemption();
  const { resolveVoucher } = useVoucher();
  const { applyDiscountToReservation, removeDiscountFromReservation } = useTemporaryResevation();
  const [voucherCartSummary, setVoucherCartSummary] = useState(null);
  const [voucherPinVisible, setVoucherPinVisible] = useState(false);
  const [voucherPinRequired, setVoucherPinRequired] = useState(null);
  const [discountInfo, setDiscountInfo] = useState(null); // { code, percent, forceOnlinePayment }
  const [discountError, setDiscountError] = useState('');
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountCartLineItems, setDiscountCartLineItems] = useState(null); // line items from cart summary after discount

  const [hoveredRowIndex, setHoveredRowIndex] = useState(null);
  const [editingPriceIndex, setEditingPriceIndex] = useState(null);
  const [editedPrice, setEditedPrice] = useState('');
  // After first focus, keep the multiline comment field tall until temp reservation changes or modal unmounts.
  const [keepCommentExpanded, setKeepCommentExpanded] = useState(false);
  const [openAfterSave, setOpenAfterSave] = useState(preferences?.openBookingAfterCreate !== false);
  const [disableSelfService, setDisableSelfService] = useState(false);
  const [suppressCustomerEmails, setSuppressCustomerEmails] = useState(false);
  // Customer wants to pay online instead of on site: the booking is created as an online
  // booking and the customer receives a payment link ("Bezahllink") by mail. The on-site
  // service fee does not apply then, so it drops out of the cart below.
  const [onlinePayment, setOnlinePayment] = useState(false);
  const [capacityOverrideEnabled, setCapacityOverrideEnabled] = useState(false);
  const manualSelfServiceToggleRef = useRef(false);

  // Default the self-service toggle to off for B2B products (tag-driven). A manual
  // choice always wins — also across product changes, so the switch keeps its value
  // when further entertainments are added to the cart and does not have to be set
  // again for every item.
  useEffect(() => {
    if (!checkRightAny('internalBooking:special:deactivateSelfService')) return;
    if (!selectedProduct) return;
    if (manualSelfServiceToggleRef.current) return;

    const productId = String(selectedProduct);

    const selectedProductObject = Array.isArray(products)
      ? products.find(p => String(p?._id) === productId)
      : null;

    const rawTags = selectedProductObject?.tags ?? [];
    const tags = Array.isArray(rawTags)
      ? rawTags.map(t => String(t).toLowerCase())
      : typeof rawTags === 'string'
        ? rawTags
            .split(',')
            .map(t => t.trim().toLowerCase())
            .filter(Boolean)
        : [];

    const shouldDisableSelfService = tags.includes('b2b');

    setDisableSelfService(shouldDisableSelfService);
  }, [checkRightAny, products, selectedProduct]);

  const localLineItems = getCurrentLineItems();
  const getLineItemReferenceId = item => item?.referenceId ?? item?.categoryName ?? item?.id;
  const applyLineItemOverride = item => {
    const referenceId = getLineItemReferenceId(item);
    if (referenceId !== undefined && priceOverrides[referenceId] !== undefined) {
      return {
        ...item,
        referenceId,
        originalPrice: item.originalPrice ?? item.price,
        price: priceOverrides[referenceId],
      };
    }
    return {
      ...item,
      referenceId,
    };
  };
  const sourceCartLineItems =
    discountCartLineItems ||
    voucherCartSummary?.lineItems ||
    reservationCartSummary?.lineItems ||
    null;
  const lineItems = Array.isArray(sourceCartLineItems)
    ? sourceCartLineItems
        .filter(item => item.type !== 'voucherRedemption')
        .map(item => applyLineItemOverride(item))
    : localLineItems;

  const formatPrice = price => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  const handleRestoreAllOverrides = () => {
    setPriceOverrides({});
    setPriceOverride(false);
    setSpecialLineItems([]);
  };

  const handlePriceOverrideChange = e => {
    const newValue = e.target.checked;
    setPriceOverride(newValue);
    if (!newValue) {
      // clear special line items when price override is turned off
      setSpecialLineItems([]);
    }
  };

  const handlePriceChange = e => {
    setEditedPrice(e.target.value);
  };

  const handlePriceSubmit = item => {
    if (editedPrice !== '') {
      const newPrice = parseFloat(editedPrice.replace('.', '').replace(',', '.'));
      if (!isNaN(newPrice)) {
        updateLineItemPrice(item.referenceId, newPrice);
      }
    }

    setEditingPriceIndex(null);
    setEditedPrice('');
  };

  const handlePriceBlur = item => {
    handlePriceSubmit(item);
  };

  const handleKeyPress = (e, item) => {
    if (e.key === 'Enter') {
      handlePriceSubmit(item);
    }
  };

  const handleEditClick = (item, index) => {
    if (!priceOverride) return;
    setEditingPriceIndex(index);
    const price = priceOverrides[item.referenceId] ?? item.price;
    setEditedPrice(price.toFixed(2).replace('.', ','));
  };

  const handleAddSpecialLineItem = () => {
    const newSpecialItem = {
      description: '',
      count: 1,
      price: 0,
    };
    setSpecialLineItems([...specialLineItems, newSpecialItem]);
  };

  const handleUpdateSpecialLineItem = (index, updatedItem) => {
    const updatedItems = [...specialLineItems];
    updatedItems[index] = updatedItem;
    setSpecialLineItems(updatedItems);
  };

  const handleDeleteSpecialLineItem = index => {
    const updatedItems = specialLineItems.filter((_, i) => i !== index);
    setSpecialLineItems(updatedItems);
  };

  const hasIncompleteSpecialItems = specialLineItems.some(
    item => !item.description.trim() || item.count <= 0 || item.price <= 0
  );

  const entertainmentDisplay =
    selectedEntertainmentObject?.longName ||
    selectedEntertainmentObject?.entertainmentName ||
    selectedEntertainmentObject?.name ||
    '-';
  // Internal bookings are always completed with on-site ("offline") payment, so the service fee
  // (Servicepauschale Vor-Ort-Zahlung) configured on the entertainment/product normally applies.
  // The backend adds it at conversion unless a value voucher forces online payment or the booking
  // is a walk-in, so mirror that exact condition here to keep the displayed cart and total in sync
  // with the amount the backend calculates. Walk-in customers are already at the venue and must
  // never be charged the fee. getCartSummary returns the resolved fee as a top-level field even
  // when the (online) summary line items do not yet contain a dedicated fee position.
  const appliedValueVouchers = Array.isArray(voucherCartSummary?.appliedVouchers)
    ? voucherCartSummary.appliedVouchers
    : [];
  const offlinePaymentFeeValue = Number(
    String(
      voucherCartSummary?.offlinePaymentFee ?? reservationCartSummary?.offlinePaymentFee ?? 0
    ).replace(',', '.')
  );
  // On-site payment is only available for phone numbers inside the DACH/Benelux whitelist: an
  // unpaid no-show cannot be pursued abroad, so such a booking has to go out as a payment link.
  // Walk-ins are exempt — the customer is at the counter — and the whitelist right lifts it.
  const onlinePaymentForced =
    !customerData?.isWalkIn &&
    !checkRightAny('internalBooking:special:anyPhoneNumber') &&
    !isOfflinePaymentAllowedForPhone(customerData?.phone);
  const effectiveOnlinePayment = onlinePayment || onlinePaymentForced;

  const showOfflinePaymentFee =
    !customerData?.isWalkIn &&
    !effectiveOnlinePayment &&
    appliedValueVouchers.length === 0 &&
    Number.isFinite(offlinePaymentFeeValue) &&
    offlinePaymentFeeValue > 0;

  const payableTotal = (() => {
    const lineItemTotal = lineItems.reduce(
      (sum, item) => sum + Number(item.price || 0) * Number(item.count || 0),
      0
    );
    const specialTotal = specialLineItems.reduce(
      (sum, item) => sum + Number(item.price || 0) * Number(item.count || 0),
      0
    );
    const voucherTotal = Number(String(voucherCartSummary?.voucherTotal || 0).replace(',', '.'));
    const offlineFee = showOfflinePaymentFee ? offlinePaymentFeeValue : 0;
    return Math.max(
      0,
      lineItemTotal + specialTotal + offlineFee - (Number.isFinite(voucherTotal) ? voucherTotal : 0)
    );
  })();

  useImperativeHandle(ref, () => ({
    getComment: () => commentRef.current?.value || '',
    getOpenAfterSave: () => openAfterSave,
    getDisableSelfService: () => disableSelfService,
    getSuppressCustomerEmails: () => suppressCustomerEmails,
    getOnlinePayment: () => effectiveOnlinePayment,
    getCapacityParticipantCount: () => {
      if (!capacityOverrideEnabled) return undefined;
      const raw = capacityParticipantCountRef.current?.value?.trim?.();
      if (!raw) return undefined;
      const n = Number(raw);
      return Number.isFinite(n) && n >= 0 ? n : undefined;
    },
    getCapacityLaneCount: () => {
      if (!capacityOverrideEnabled) return undefined;
      const raw = capacityLaneCountRef.current?.value?.trim?.();
      if (!raw) return undefined;
      const n = Number(raw);
      return Number.isFinite(n) && n >= 0 ? n : undefined;
    },
    getPayableTotalAmount: () => payableTotal,
  }));

  useEffect(() => {
    setVoucherCartSummary(null);
    setVoucherPinVisible(false);
    setVoucherPinRequired(null);
    setKeepCommentExpanded(false);
    setDiscountInfo(null);
    setDiscountError('');
    setDiscountCartLineItems(null);
  }, [tempReservationId]);

  const applyVoucherAndUpdate = async (voucherCode, pin) => {
    try {
      const res = await applyVoucher(tempReservationId, voucherCode, pin || undefined);
      const cs = res?.cartSummary || res?.data?.cartSummary || null;
      if (cs) {
        setVoucherCartSummary(cs);
        if (redeemCodeRef.current) redeemCodeRef.current.value = '';
        if (voucherPinRef.current) voucherPinRef.current.value = '';
        setVoucherPinVisible(false);
        setVoucherPinRequired(null);
      }
    } catch (_) {
      // toast handled in hook
    }
  };

  // Combined discount/voucher input: voucher codes are machine-generated, so an exact
  // existence check via /voucher/resolve decides the path (and whether a PIN is needed);
  // everything else is treated as a discount code.
  const handleApplyCode = async () => {
    if (!tempReservationId) {
      toast.error('Kein Reservierungsdatensatz gefunden');
      return;
    }
    const code = redeemCodeRef.current?.value?.trim?.() || '';
    const pin = voucherPinRef.current?.value?.trim?.() || '';
    if (!code) return;

    if (voucherPinVisible) {
      if (voucherPinRequired && !pin) return;
      await applyVoucherAndUpdate(code, pin);
      return;
    }

    setDiscountError('');
    let resolved = null;
    try {
      resolved = await resolveVoucher(code);
    } catch (_) {
      resolved = null; // resolve failure falls back to the discount path
    }
    if (resolved?.exists) {
      const requiresPin = Boolean(resolved.requiresPin);
      setVoucherPinRequired(requiresPin);
      if (requiresPin) {
        setVoucherPinVisible(true);
        return;
      }
      await applyVoucherAndUpdate(code, '');
      return;
    }

    await handleApplyDiscount();
  };

  const handleRemoveVoucher = async voucherId => {
    if (!tempReservationId) return;
    try {
      const res = await removeVoucher(tempReservationId, voucherId);
      const cs = res?.cartSummary || res?.data?.cartSummary || null;
      setVoucherCartSummary(cs || null);
    } catch (_) {
      // toast handled in hook
    }
  };

  const getDiscountErrorMessage = err => {
    const code = err?.code || err?.response?.data?.code || '';
    const message = err?.message || err?.response?.data?.message || '';
    switch (code) {
      case 'DISCOUNT_CODE_REQUIRES_INDIVIDUAL':
        return 'Dieser Rabattcode kann nicht direkt eingelöst werden.';
      case 'INDIVIDUAL_CODE_ALREADY_REDEEMED':
        return 'Dieser Code wurde bereits eingelöst.';
      case 'INDIVIDUAL_CODE_EXPIRED':
        return 'Dieser Code ist abgelaufen.';
      case 'DISCOUNT_CODE_AVAILABILITY_MISMATCH':
        return 'Dieser Rabattcode gilt nicht für den gewählten Zeitpunkt.';
      default: {
        // Backend sends German messages — use them directly if available.
        // useHttp prefixes with "STATUS: ", so strip that prefix to get the original message.
        const cleaned = message.replace(/^\d{3}:\s*/, '');
        if (cleaned) return cleaned;
        return 'Fehler beim Einlösen des Rabattcodes';
      }
    }
  };

  const handleApplyDiscount = async () => {
    if (!tempReservationId) {
      toast.error('Kein Reservierungsdatensatz gefunden');
      return;
    }
    const code = redeemCodeRef.current?.value?.trim?.() || '';
    if (!code) return;

    setDiscountError('');
    setDiscountLoading(true);
    try {
      const res = await applyDiscountToReservation(tempReservationId, code);
      const cs = res?.cartSummary || res?.data?.cartSummary || null;
      setDiscountInfo({
        code: cs?.appliedDiscountCode || code,
        percent: cs?.discountPercent || null,
        forceOnlinePayment: Boolean(cs?.forceOnlinePayment),
        userHint: cs?.discountUserHint || null,
      });
      if (cs) {
        // Store discounted line items from backend for strikethrough display
        if (Array.isArray(cs.lineItems)) setDiscountCartLineItems(cs.lineItems);
        setVoucherCartSummary(prev => ({ ...(prev || {}), ...cs, appliedVouchers: prev?.appliedVouchers || cs?.appliedVouchers }));
      }
      if (redeemCodeRef.current) redeemCodeRef.current.value = '';
    } catch (err) {
      const msg = getDiscountErrorMessage(err);
      setDiscountError(msg);
      toast.error(msg);
    } finally {
      setDiscountLoading(false);
    }
  };

  const handleRemoveDiscount = async () => {
    if (!tempReservationId) return;
    setDiscountLoading(true);
    try {
      const res = await removeDiscountFromReservation(tempReservationId);
      const cs = res?.cartSummary || res?.data?.cartSummary || null;
      setDiscountInfo(null);
      setDiscountError('');
      setDiscountCartLineItems(null);
      if (cs) setVoucherCartSummary(prev => ({ ...(prev || {}), ...cs, appliedVouchers: prev?.appliedVouchers || cs?.appliedVouchers }));
    } catch (err) {
      toast.error('Fehler beim Entfernen des Rabattcodes');
    } finally {
      setDiscountLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Typography
          variant='h6'
          sx={{ fontSize: '1.7rem', fontWeight: 'bold', mb: { xs: 2, sm: 0 } }}
        >
          Buchungsübersicht
        </Typography>
        {checkRightAny('internalBooking:special:overridePrices') && (
          <Stack direction='row' spacing={2} sx={{ mb: { xs: 2, sm: 0 } }}>
            <LockIconWrapper right='internalBooking:special:overridePrices'>
              <FormControlLabel
                control={
                  <Switch
                    checked={priceOverride}
                    onChange={handlePriceOverrideChange}
                    color='success'
                  />
                }
                label='Preis überschreiben'
              />
            </LockIconWrapper>
            {priceOverride && Object.keys(priceOverrides).length > 0 && (
              <IconButton size='small' onClick={handleRestoreAllOverrides}>
                <SettingsBackupRestoreIcon fontSize='small' />
              </IconButton>
            )}
          </Stack>
        )}
      </Box>

      <Box mb={2}>
        <Typography variant='subtitle2'>Kundendaten</Typography>
        <Typography variant='body1'>
          Kunde: {customerData.firstName} {customerData.lastName}
        </Typography>
        {customerData.companyName && (
          <Typography variant='body1'>Firma: {customerData.companyName}</Typography>
        )}
        <Typography>Telefon: {customerData?.phone}</Typography>
        <Typography>E-Mail: {customerData?.mail}</Typography>
        <Typography>
          Adresse: {customerData?.street}, {customerData?.postcode} {customerData?.city}
        </Typography>
        <Typography>
          Geburtsdatum:{' '}
          {customerData?.birthdate ? dayjs(customerData.birthdate).format('DD.MM.YYYY') : '-'}
        </Typography>
      </Box>

      <Divider />

      <Stack spacing={2} mt={2}>
        <Box>
          <Typography variant='subtitle2'>Termin</Typography>
          <Typography>Entertainment: {entertainmentDisplay}</Typography>
          {selectedResource && (
            <Typography>{resources.find(r => r._id === selectedResource)?.name || ''}</Typography>
          )}
          <Typography>
            {selectedDate ? dayjs(selectedDate).format('DD.MM.YYYY') : '-'} um{' '}
            {selectedTime + ' Uhr' || '-'}
          </Typography>
          <Typography>Dauer: {formatDuration(selectedDuration)}</Typography>
        </Box>

        <Divider />

        <Box>
          <Typography variant='subtitle2'>Teilnehmer</Typography>
          {selectedCategories.map((category, index) => (
            <Typography key={index}>
              {category.name}: {category.quantity}
            </Typography>
          ))}
        </Box>

        <Divider />

        {(selectedPackage || (selectedUpsells && selectedUpsells.length > 0)) && (
          <>
            <Box>
              <Typography variant='subtitle2'>Paket & Zusatzleistungen</Typography>
              {selectedPackage && <Typography>Paket: {selectedPackage.name}</Typography>}
              {selectedUpsells && selectedUpsells.length > 0 && (
                <Typography>
                  Zusatzleistungen: {selectedUpsells.map(upsell => upsell.name).join(', ')}
                </Typography>
              )}
            </Box>

            <Divider />
          </>
        )}

        {lineItems && lineItems.length > 0 && (
          <Box>
            <TableContainer component={Paper}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Beschreibung</TableCell>
                    <TableCell align='right'>Preis</TableCell>
                    <TableCell align='right'>Anzahl</TableCell>
                    <TableCell align='right'>Gesamt</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineItems.map((item, index) => (
                    <TableRow
                      key={`${item.id}-${index}`}
                      onMouseEnter={() => setHoveredRowIndex(index)}
                      onMouseLeave={() => setHoveredRowIndex(null)}
                      onClick={() => handleEditClick(item, index)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <TableCell align='left'>{item.description}</TableCell>
                      <TableCell align='right' sx={{ position: 'relative', minWidth: '120px' }}>
                        {editingPriceIndex === index ? (
                          <TextField
                            value={editedPrice}
                            onChange={handlePriceChange}
                            onBlur={() => handlePriceBlur(item)}
                            onKeyPress={e => handleKeyPress(e, item)}
                            size='small'
                            autoFocus
                            sx={{
                              width: '100px',
                              '& .MuiOutlinedInput-root': {
                                '&.Mui-focused fieldset': {
                                  borderColor: 'success.main',
                                },
                              },
                            }}
                            inputProps={{
                              style: { textAlign: 'right' },
                            }}
                          />
                        ) : (
                          <>
                            {item.originalPrice != null && item.originalPrice !== item.price ? (
                              <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                <Typography
                                  variant='body2'
                                  component='span'
                                  sx={{ textDecoration: 'line-through', color: 'text.disabled', fontSize: '0.75rem', lineHeight: 1 }}
                                >
                                  {formatPrice(priceOverrides[item.referenceId] ?? item.originalPrice)}
                                </Typography>
                                <Typography variant='body2' component='span' sx={{ color: 'success.main', fontWeight: 'bold' }}>
                                  {formatPrice(priceOverrides[item.referenceId] ?? item.price)}
                                </Typography>
                              </Box>
                            ) : (
                              formatPrice(priceOverrides[item.referenceId] ?? item.price)
                            )}
                            {hoveredRowIndex === index && priceOverride && (
                              <IconButton
                                size='small'
                                onClick={e => {
                                  e.stopPropagation();
                                  handleEditClick(item, index);
                                }}
                                sx={{
                                  position: 'absolute',
                                  right: '-40px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  padding: '2px',
                                }}
                              >
                                <EditIcon fontSize='small' />
                              </IconButton>
                            )}
                          </>
                        )}
                      </TableCell>
                      <TableCell align='right'>{item.count}</TableCell>
                      <TableCell align='right'>
                        {item.originalPrice != null && item.originalPrice !== item.price ? (
                          <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <Typography
                              variant='body2'
                              component='span'
                              sx={{ textDecoration: 'line-through', color: 'text.disabled', fontSize: '0.75rem', lineHeight: 1 }}
                            >
                              {formatPrice((priceOverrides[item.referenceId] ?? item.originalPrice) * item.count)}
                            </Typography>
                            <Typography variant='body2' component='span' sx={{ color: 'success.main', fontWeight: 'bold' }}>
                              {formatPrice((priceOverrides[item.referenceId] ?? item.price) * item.count)}
                            </Typography>
                          </Box>
                        ) : (
                          formatPrice((priceOverrides[item.referenceId] ?? item.price) * item.count)
                        )}
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* Offline payment service fee (Servicepauschale Vor-Ort-Zahlung). Internal
                      bookings are always paid on-site, so surface the fee directly in the cart
                      instead of only adding it silently at conversion. */}
                  {showOfflinePaymentFee && (
                    <TableRow>
                      <TableCell align='left'>Servicepauschale Vor-Ort-Zahlung</TableCell>
                      <TableCell align='right'>{formatPrice(offlinePaymentFeeValue)}</TableCell>
                      <TableCell align='right'>1</TableCell>
                      <TableCell align='right'>{formatPrice(offlinePaymentFeeValue)}</TableCell>
                    </TableRow>
                  )}

                  {/* Special Line Items */}
                  {priceOverride &&
                    specialLineItems.map((specialItem, index) => (
                      <SpecialLineItem
                        key={`special-${index}`}
                        item={specialItem}
                        index={index}
                        onUpdate={handleUpdateSpecialLineItem}
                        onDelete={handleDeleteSpecialLineItem}
                      />
                    ))}

                  {/* Applied Vouchers as negative line items */}
                  {Array.isArray(voucherCartSummary?.appliedVouchers) &&
                    voucherCartSummary.appliedVouchers.map((v, idx) => (
                      <TableRow
                        key={`voucher-${v.voucherId || idx}`}
                        sx={{ backgroundColor: 'rgba(76, 175, 80, 0.08)' }}
                      >
                        <TableCell align='left'>
                          Gutschein {v.voucherCode}
                        </TableCell>
                        <TableCell align='right' sx={{ color: 'success.main' }}>
                          {formatPrice(-(Number(v.amountApplied) || 0))}
                        </TableCell>
                        <TableCell align='right'>1</TableCell>
                        <TableCell align='right' sx={{ color: 'success.main' }}>
                          {formatPrice(-(Number(v.amountApplied) || 0))}
                          <IconButton
                            size='small'
                            color='error'
                            onClick={() => handleRemoveVoucher(v.voucherId)}
                            sx={{ ml: 1 }}
                          >
                            <SettingsBackupRestoreIcon fontSize='small' />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}

                  {/* Add Special Line Item Button */}
                  {priceOverride && (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ padding: 0 }}>
                        <Box
                          onClick={handleAddSpecialLineItem}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '53px', // Same height as a normal table row
                            cursor: 'pointer',
                            border: '2px dashed #ccc',
                            borderRadius: 1,
                            margin: 1,
                            '&:hover': {
                              borderColor: 'primary.main',
                              backgroundColor: 'rgba(0, 0, 0, 0.02)',
                            },
                          }}
                        >
                          <IconButton size='small' color='primary'>
                            <AddIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {hasIncompleteSpecialItems && (
          <Alert severity='warning' sx={{ mb: 2 }}>
            Du hast eigene Items hinzugefügt, bei denen noch nicht alle Informationen eingegeben
            wurden!
          </Alert>
        )}

        <Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box sx={{ flex: '0 1 auto', minWidth: 0 }}>
              <Stack direction='row' spacing={1} alignItems='flex-start' flexWrap='wrap' useFlexGap>
                {discountInfo && (
                  <Chip
                    icon={<DiscountIcon />}
                    label={
                      <span>
                        Rabattcode <strong>{discountInfo.code}</strong>
                        {discountInfo.percent ? ` · −${discountInfo.percent}%` : ''}
                      </span>
                    }
                    color='success'
                    variant='outlined'
                    onDelete={handleRemoveDiscount}
                    deleteIcon={<DeleteIcon />}
                    disabled={discountLoading}
                    sx={{ height: 36, px: 0.5, '& .MuiChip-label': { px: 1 } }}
                  />
                )}
                <TextField
                  size='small'
                  inputRef={redeemCodeRef}
                  placeholder={discountInfo ? 'Gutscheincode' : 'Rabatt- / Gutscheincode'}
                  error={Boolean(discountError)}
                  helperText={discountError || ''}
                  onChange={() => {
                    if (voucherPinVisible) {
                      setVoucherPinVisible(false);
                      setVoucherPinRequired(null);
                    }
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleApplyCode();
                    }
                  }}
                  disabled={discountLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <DiscountIcon fontSize='small' color='action' />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: { xs: '100%', sm: 220 } }}
                />
                {voucherPinVisible && (
                  <TextField
                    size='small'
                    inputRef={voucherPinRef}
                    placeholder='PIN'
                    autoFocus
                    helperText='Dieser Gutschein ist PIN-geschützt'
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleApplyCode();
                      }
                    }}
                    sx={{ width: { xs: '100%', sm: 120 } }}
                  />
                )}
                <Button
                  variant='contained'
                  color='success'
                  onClick={handleApplyCode}
                  disabled={discountLoading}
                  sx={{ minWidth: 110, height: 40 }}
                >
                  {discountLoading ? 'Prüfe...' : 'Einlösen'}
                </Button>
              </Stack>
            </Box>
            <Box sx={{ textAlign: 'right', mr: { xs: 0, sm: 2 } }}>
              <Typography variant='subtitle2'>Gesamtpreis</Typography>
              <Typography variant='body1' fontWeight='bold'>
                {formatPrice(payableTotal)}
              </Typography>
            </Box>
          </Box>
          {discountInfo?.userHint && (
            <Alert severity='info' sx={{ mt: 1.5 }}>
              {discountInfo.userHint}
            </Alert>
          )}
          {discountInfo?.forceOnlinePayment && (
            <Alert severity='warning' sx={{ mt: 1.5 }}>
              Dieser Rabattcode erfordert eigentlich Online-Zahlung. Bei interner Buchung wird er
              trotzdem angewendet — bitte ggf. separat abrechnen.
            </Alert>
          )}
        </Box>

        <Divider />

        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={openAfterSave}
                onChange={e => setOpenAfterSave(e.target.checked)}
                color='success'
                size='small'
              />
            }
            label='Buchung nach dem Speichern öffnen'
          />
        </Box>

        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={effectiveOnlinePayment}
                onChange={e => setOnlinePayment(e.target.checked)}
                disabled={onlinePaymentForced}
                color='primary'
                size='small'
              />
            }
            label='Kunde möchte online bezahlen (Bezahllink per E-Mail)'
          />
          {onlinePaymentForced && (
            <Typography variant='caption' color='warning.main' display='block' sx={{ ml: 6 }}>
              Mit dieser Telefonnummer ist nur Online-Zahlung möglich. Zahlung vor Ort ist nur für
              Nummern aus Deutschland, Österreich, der Schweiz und den Benelux-Ländern verfügbar.
              Der Kunde erhält einen Bezahllink und kann dort nicht auf Zahlung vor Ort wechseln.
            </Typography>
          )}
          {effectiveOnlinePayment && !onlinePaymentForced && (
            <Typography variant='caption' color='text.secondary' display='block' sx={{ ml: 6 }}>
              Der Kunde erhält einen Bezahllink und kann dort auch auf Zahlung vor Ort wechseln.
              Die Buchung bleibt in jedem Fall bestehen: Wird bis 24 Stunden vor dem Termin nicht
              online bezahlt, stellt das System sie automatisch auf Zahlung vor Ort um und
              informiert den Kunden.
              {offlinePaymentFeeValue > 0 && !customerData?.isWalkIn
                ? ` Bei Online-Zahlung entfällt die Servicepauschale Vor-Ort-Zahlung (${formatPrice(offlinePaymentFeeValue)}), bei der Umstellung wird sie berechnet.`
                : ''}
            </Typography>
          )}
        </Box>

        {checkRightAny('internalBooking:special:deactivateSelfService') && (
          <Box>
            <LockIconWrapper right='internalBooking:special:deactivateSelfService'>
              <FormControlLabel
                control={
                  <Switch
                    checked={disableSelfService}
                    onChange={e => {
                      manualSelfServiceToggleRef.current = true;
                      setDisableSelfService(e.target.checked);
                    }}
                    color='warning'
                    size='small'
                  />
                }
                label='Kundenbearbeitung deaktivieren (kein Self-Service Link in E-Mail)'
              />
            </LockIconWrapper>
          </Box>
        )}

        {checkRightAny('internalBooking:special:suppressEmails') && (
          <Box>
            <LockIconWrapper right='internalBooking:special:suppressEmails'>
              <FormControlLabel
                control={
                  <Switch
                    checked={suppressCustomerEmails}
                    onChange={e => setSuppressCustomerEmails(e.target.checked)}
                    color='error'
                    size='small'
                  />
                }
                label='Keine Kunden-E-Mails senden (Bestätigung, Änderungen etc.)'
              />
            </LockIconWrapper>
          </Box>
        )}

        {checkRightAny('internalBooking:special:overrideCapacity') && (
          <Box>
            <LockIconWrapper right='internalBooking:special:overrideCapacity'>
              <Stack
                direction='row'
                alignItems='center'
                flexWrap='wrap'
                sx={{ gap: { xs: 1, sm: 2 } }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={capacityOverrideEnabled}
                      onChange={e => setCapacityOverrideEnabled(e.target.checked)}
                      color='primary'
                      size='small'
                    />
                  }
                  label='Kapazitätsübersteuerung'
                  sx={{ mr: 0 }}
                />
                {capacityOverrideEnabled && (
                  <>
                    <TextField
                      inputRef={capacityParticipantCountRef}
                      label='Teilnehmer'
                      type='number'
                      size='small'
                      variant='outlined'
                      inputProps={{ min: 0, step: 1 }}
                      placeholder='Standard'
                      sx={{ width: 130 }}
                    />
                    <TextField
                      inputRef={capacityLaneCountRef}
                      label='Bahnen'
                      type='number'
                      size='small'
                      variant='outlined'
                      inputProps={{ min: 0, step: 1 }}
                      placeholder='Standard'
                      sx={{ width: 130 }}
                    />
                  </>
                )}
              </Stack>
            </LockIconWrapper>
          </Box>
        )}

        <Box>
          <TextField
            fullWidth
            multiline
            rows={keepCommentExpanded ? 10 : 2}
            placeholder='Kommentar zur Buchung (optional)'
            variant='outlined'
            size='small'
            inputRef={commentRef}
            onFocus={() => setKeepCommentExpanded(true)}
          />
        </Box>
      </Stack>
    </Box>
  );
});

export default Overview;
